import { UzMultiSelect } from '@utazzo/ui';
import React, { useMemo, useState } from 'react';

import { ChannelKpis } from './ChannelKpis';
import { QueryChannelTable } from './QueryChannelTable';

import type {
  MarketingCategorySlice,
  MarketingChannel,
  MarketingChannelMonth,
  MarketingChannelResponse,
} from '@utazzo/shared';

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Merge slices by summing the raw counts and RE-deriving the ratios. Averaging
 * the percentages instead would weight a 10-lead channel the same as a
 * 1,000-lead one and quietly produce a wrong conversion rate.
 */
const mergeSlices = (slices: MarketingCategorySlice[]): MarketingCategorySlice => {
  const query = slices.reduce((sum, slice) => sum + slice.query, 0);
  const converted = slices.reduce((sum, slice) => sum + slice.converted, 0);
  const ttvAmount = slices.reduce((sum, slice) => sum + (slice.ttvAmount ?? 0), 0);
  return {
    query,
    converted,
    ttvAmount: round2(ttvAmount),
    conversionPct: query > 0 ? round2((converted / query) * 100) : null,
    avgOrderValue: converted > 0 ? round2(ttvAmount / converted) : null,
  };
};

/**
 * Sum several month series (one per picked channel / sub-channel) into a single
 * series, month by month. The caller is responsible for handing over series that
 * do not overlap - see `resolveSelection`, which drops a sub-source whenever its
 * parent is also picked so nothing is counted twice.
 */
const combineMonths = (series: MarketingChannelMonth[][]): MarketingChannelMonth[] => {
  const first = series[0];
  if (!first) return [];
  return first.map((_, index) => {
    const slice = (pick: (m: MarketingChannelMonth) => MarketingCategorySlice) =>
      mergeSlices(series.map((months) => pick(months[index]!)));
    return {
      month: first[index]!.month,
      salesProfit: round2(
        series.reduce((sum, months) => sum + (months[index]!.salesProfit ?? 0), 0),
      ),
      overall: slice((m) => m.overall),
      international: slice((m) => m.international),
      domestic: slice((m) => m.domestic),
    };
  });
};

/**
 * Synthetic "All channels" series. Safe to build by summing TOP-LEVEL channels
 * only: every lead carries exactly one source (or none, which lands in the
 * "Not attributed" channel), so the top level partitions the leads. Sub-sources
 * are slices of their parents and would double-count if included.
 */
const combineChannels = (channels: MarketingChannel[]): MarketingChannelMonth[] =>
  combineMonths(channels.map((channel) => channel.months));

/**
 * Turn the picked keys into the one series the tiles summarise, plus the label
 * that names it.
 *
 * Two rules keep the total honest:
 *  - a sub-source whose PARENT is also picked is dropped (it is a slice of that
 *    parent, so adding both would count those leads twice);
 *  - an empty pick means "everything", not "nothing" - the tab opens on the full
 *    picture and clearing the filter returns to it.
 */
const resolveSelection = (
  list: MarketingChannel[],
  selected: string[],
): { name: string; months: MarketingChannelMonth[] } => {
  const all = { name: 'All channels', months: combineChannels(list) };
  if (selected.length === 0) return all;

  const picked = new Set(selected);
  const parts: Array<{ name: string; months: MarketingChannelMonth[] }> = [];
  for (const channel of list) {
    if (picked.has(channel.key)) {
      parts.push({ name: channel.name, months: channel.months });
      continue;
    }
    for (const child of channel.children) {
      if (picked.has(child.key)) {
        parts.push({ name: `${channel.name} › ${child.name}`, months: child.months });
      }
    }
  }

  // Everything picked has since vanished (month range changed under us).
  if (parts.length === 0) return all;
  if (parts.length === 1) return parts[0]!;
  return {
    name:
      parts.length <= 2 ? parts.map((part) => part.name).join(' + ') : `${parts.length} channels`,
    months: combineMonths(parts.map((part) => part.months)),
  };
};

/**
 * "Query Channel" tab body: a platform picker, KPI tiles for whatever is picked,
 * and the detail table below scoped to the same selection.
 *
 * The picker is MULTI-select: comparing, say, Instagram against Google without
 * the rest of the mix is a real ask, and one-at-a-time could not express it.
 * Nothing picked = all channels, so the tab opens on the full picture; picking
 * narrows the tiles and the table together, so the two can never disagree about
 * what is on screen.
 */
export const QueryChannelPanel = ({
  channels,
  loading,
  showKpis = true,
}: {
  channels: MarketingChannelResponse | undefined;
  loading: boolean;
  /** The per-channel tiles. Off on the Overview, which has its own at the top. */
  showKpis?: boolean;
}): React.JSX.Element => {
  const [selected, setSelected] = useState<string[]>([]);

  const list = useMemo(() => channels?.channels ?? [], [channels]);

  const options = useMemo(
    () =>
      list.flatMap((channel) => [
        { value: channel.key, label: channel.name },
        // Sub-sources are pickable too, indented so the hierarchy reads.
        ...channel.children.map((child) => ({
          value: child.key,
          label: `   ${child.name}`,
          description: channel.name,
        })),
      ]),
    [list],
  );

  const view = useMemo(() => resolveSelection(list, selected), [list, selected]);

  // A picked sub-source is folded into its parent when both are picked; say so
  // rather than leaving the operator to wonder why the total did not move.
  const subsumed = useMemo(
    () =>
      list.some(
        (channel) =>
          selected.includes(channel.key) &&
          channel.children.some((child) => selected.includes(child.key)),
      ),
    [list, selected],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <UzMultiSelect
          label="Platform"
          placeholder="All channels"
          searchPlaceholder="Search channels…"
          values={selected}
          onValuesChange={setSelected}
          options={options}
          containerClassName="w-full max-w-sm"
          maxChips={2}
          hint={
            subsumed
              ? 'Sub-sources inside a picked channel are already counted in it.'
              : 'Filters the tiles and the table below. Leave empty for all channels.'
          }
        />
      </div>

      {showKpis && (
        <ChannelKpis
          channelName={view.name}
          month={view.months[0]}
          monthLabel={channels?.months[0]?.label}
          loading={loading}
        />
      )}

      <QueryChannelTable channels={channels} loading={loading} filterKeys={selected} />
    </div>
  );
};
